/**
 * AudioSyncHandler.js - CLIENT-SIDE AUDIO SYNCHRONIZATION
 * 
 * Smart sync logic:
 * 1. Check current playback state FIRST
 * 2. Only seek if timestamp differs significantly (>3 seconds)
 * 3. Only update play/pause if state is different from current
 * 4. Prevent duplicate commands
 */

class AudioSyncHandler {
  constructor() {
    // Sync threshold - seek if more than 0.5 seconds off for tight sync
    this.SEEK_THRESHOLD_SEC = 0.5;
    
    // State tracking
    
    // Timestamp when INITIAL_SYNC was last processed — used for SYNC_UPDATE cooldown
    this._initialSyncTime = 0;
    // Cooldown period (ms) to skip SYNC_UPDATE after INITIAL_SYNC
    this._initialSyncCooldown = 5000;
    // Rate limit for resume attempts when guest is paused but should be playing
    this._lastResumeAttempt = 0;
    this.isHost = false;
    this.sessionId = null;
    this.isSyncing = false;
    this.lastTrackId = null;
    this.isPausedByHost = false; // Track if host paused - prevents auto-play
    this._isLoadingTrack = false; // Guard against concurrent MusicKit operations
    
    // Message queue for handling messages that arrive during sync
    this._messageQueue = [];
    
    // Callbacks
    this.onPlay = null;
    this.onPause = null;
    this.onSeek = null;
    this.onTrackChange = null;
    
    // Debug
    this.debugMode = true;
  }
  
  log(message) {
    if (this.debugMode) {
      console.log(`[AudioSync] ${message}`);
    }
  }
  
  init(sessionId, isHost) {
    this.sessionId = sessionId;
    this.isHost = isHost;
    this.isSyncing = false;
    this.lastTrackId = null;
    this.isPausedByHost = false;
    this._lastResumeAttempt = 0;
    this._initialSyncTime = 0;
    this._isLoadingTrack = false;
    this.log(`Initialized for session ${sessionId} as ${isHost ? 'HOST' : 'GUEST'}`);
  }
  
  // Check if playback is currently paused by host
  isHostPaused() {
    return this.isPausedByHost;
  }
  
  setCallbacks({ onPlay, onPause, onSeek, onTrackChange, onTrackChangeAtPosition }) {
    this.onPlay = onPlay;
    this.onPause = onPause;
    this.onSeek = onSeek;
    this.onTrackChange = onTrackChange;
    this.onTrackChangeAtPosition = onTrackChangeAtPosition; // Load track at specific position for seamless sync
  }
  
  /**
   * Handle incoming AUDIO_SYNC message from server
   * @param {object} syncData - The sync data from server
   * @param {object} musicKitState - Current MusicKit state { isPlaying, playbackTime, currentTrackId }
   */
  async handleSyncMessage(syncData, musicKitState) {
    const { action, server_timestamp } = syncData;
    
    this.log(`===== HANDLING AUDIO_SYNC: ${action} =====`);
    this.log(`isHost: ${this.isHost}, isSyncing: ${this.isSyncing}`);
    
    // HOSTS don't need to sync - they ARE the source
    if (this.isHost) {
      this.log(`Skipping ${action} - I am the HOST`);
      return;
    }
    
    // Queue messages instead of dropping them when sync is in progress
    if (this.isSyncing) {
      this.log(`Queueing ${action} - sync in progress`);
      this._messageQueue.push({ syncData, musicKitState });
      return;
    }
    
    this.isSyncing = true;
    
    try {
      await this._processAction(action, syncData, musicKitState);
    } finally {
      this.isSyncing = false;
      // Process any queued messages
      await this._processQueue();
    }
  }
  
  /**
   * Process queued messages that arrived during a sync operation.
   * TRACK_CHANGE is prioritized over other message types because it changes
   * WHAT song is playing — if discarded, the guest stays on the wrong track.
   * SYNC_UPDATE only corrects position drift and is safe to discard.
   */
  async _processQueue() {
    while (this._messageQueue.length > 0) {
      // Prioritize TRACK_CHANGE: scan the queue for the latest one.
      // If found, process it and discard everything else.
      // If not found, process the latest message as before.
      let picked = null;
      for (let i = this._messageQueue.length - 1; i >= 0; i--) {
        if (this._messageQueue[i].syncData?.action === 'TRACK_CHANGE') {
          picked = this._messageQueue[i];
          break;
        }
      }
      if (!picked) {
        picked = this._messageQueue.pop();
      }
      this._messageQueue.length = 0; // Clear remaining
      
      if (picked) {
        // Re-read fresh MusicKit state instead of using the stale snapshot
        // from when the message was originally received and queued.
        const mk = typeof window !== 'undefined' ? window.MusicKit?.getInstance() : null;
        const freshState = {
          isPlaying: mk?.isPlaying || false,
          playbackTime: mk?.currentPlaybackTime || 0,
          currentTrackId: mk?.nowPlayingItem?.id || null,
        };
        
        this.isSyncing = true;
        try {
          await this._processAction(picked.syncData.action, picked.syncData, freshState);
        } finally {
          this.isSyncing = false;
        }
      }
    }
  }
  
  /**
   * Process a single sync action
   */
  async _processAction(action, syncData, musicKitState) {
    this.log(`Processing action: ${action}`);
    switch (action) {
      case 'INITIAL_SYNC':
        await this.handleInitialSync(syncData, musicKitState);
        break;
      case 'PLAY':
        this.log('Calling handlePlay...');
        await this.handlePlay(syncData, musicKitState);
        break;
      case 'PAUSE':
        this.log('Calling handlePause...');
        await this.handlePause(syncData, musicKitState);
        break;
      case 'SEEK':
        await this.handleSeek(syncData, musicKitState);
        break;
      case 'TRACK_CHANGE':
        await this.handleTrackChange(syncData, musicKitState);
        break;
      case 'SYNC_UPDATE':
        await this.handleSyncUpdate(syncData, musicKitState);
        break;
    }
  }
  
  /**
   * Calculate the target timestamp accounting for network delay.
   * Uses simple elapsed time: serverPosition + (now - serverTimestamp).
   * This avoids the old clockOffset formula which doubled the one-way delay.
   */
  getTargetTimestamp(serverPosition, serverTimestamp, isPlaying) {
    if (!isPlaying) return serverPosition / 1000;
    const elapsed = Math.max(0, Date.now() - serverTimestamp);
    return (serverPosition + elapsed) / 1000;
  }
  
  /**
   * Check if we need to seek based on timestamp difference
   */
  needsSeek(currentTimeSec, targetTimeSec) {
    const diff = Math.abs(currentTimeSec - targetTimeSec);
    return diff > this.SEEK_THRESHOLD_SEC;
  }
  
  /**
   * INITIAL_SYNC: Guest joining a session
   * Must work reliably on both desktop and mobile
   */
  async handleInitialSync(syncData, musicKitState) {
    const { track_id, track_info, current_position, is_playing, server_timestamp } = syncData;
    
    // Mark the time of INITIAL_SYNC — SYNC_UPDATE will be skipped during cooldown
    this._initialSyncTime = Date.now();
    
    if (!track_id) {
      this.log('INITIAL_SYNC: No track playing');
      return;
    }
    
    const targetTime = this.getTargetTimestamp(current_position, server_timestamp, is_playing);
    const currentTrackId = musicKitState?.currentTrackId;
    const currentlyPlaying = musicKitState?.isPlaying || false;
    
    this.log(`INITIAL_SYNC: Track=${track_id}, TargetTime=${targetTime.toFixed(1)}s, ShouldPlay=${is_playing}`);
    
    // Set the paused-by-host flag based on server state
    this.isPausedByHost = !is_playing;
    
    // If session is playing, we MUST start playback
    if (is_playing) {
      this.log(`INITIAL_SYNC: Session is PLAYING - loading track at position`);
      
      // Use onTrackChangeAtPosition for best sync
      if (this.onTrackChangeAtPosition) {
        this._isLoadingTrack = true;
        try {
          await this.onTrackChangeAtPosition(track_id, targetTime, track_info);
        } finally {
          this._isLoadingTrack = false;
        }
        this.log('INITIAL_SYNC: Track loaded at position - done');
        return;
      }
      
      // Fallback: Load track then seek
      if (this.onTrackChange) {
        await this.onTrackChange(track_id, track_info);
        await new Promise(r => setTimeout(r, 300));
        
        // Seek to position
        if (targetTime > 0.5 && this.onSeek) {
          try {
            await this.onSeek(targetTime);
          } catch (e) {
            this.log(`INITIAL_SYNC: Seek error: ${e.message}`);
          }
        }
        
        // Ensure playing
        if (this.onPlay && !musicKitState?.isPlaying) {
          await this.onPlay();
        }
      }
    } else {
      // Session is paused - just load the track but don't play
      this.log('INITIAL_SYNC: Session is PAUSED');
      if (track_id !== currentTrackId && this.onTrackChange) {
        await this.onTrackChange(track_id, track_info);
      }
      
      // Ensure paused
      if (currentlyPlaying && this.onPause) {
        await this.onPause();
      }
    }
  }
  
  /**
   * PLAY: Host started/resumed playback
   */
  async handlePlay(syncData, musicKitState) {
    const { track_id, track_info, start_position, server_timestamp } = syncData;
    
    this.log('=== handlePlay START ===');
    this.log(`track_id: ${track_id}, start_position: ${start_position}`);
    this.log(`Callbacks available - onPlay: ${!!this.onPlay}, onTrackChangeAtPosition: ${!!this.onTrackChangeAtPosition}`);
    
    // Clear paused flag since host is playing
    this.isPausedByHost = false;
    
    const targetTime = this.getTargetTimestamp(start_position, server_timestamp, true);
    const currentTrackId = musicKitState?.currentTrackId;
    const currentlyPlaying = musicKitState?.isPlaying || false;
    
    this.log(`Target time: ${targetTime}, current track: ${currentTrackId}, currently playing: ${currentlyPlaying}`);
    
    // If track changed, load the new track at the correct position
    if (track_id && track_id !== currentTrackId) {
      this.log(`Track changed from ${currentTrackId} to ${track_id} - loading at position ${targetTime}`);
      if (this.onTrackChangeAtPosition) {
        this._isLoadingTrack = true;
        try {
          await this.onTrackChangeAtPosition(track_id, targetTime, track_info);
        } finally {
          this._isLoadingTrack = false;
        }
        this.log('Track loaded via onTrackChangeAtPosition');
        return;
      } else if (this.onTrackChange) {
        await this.onTrackChange(track_id, track_info);
        // Wait a bit then seek
        await new Promise(r => setTimeout(r, 200));
        if (this.onSeek && targetTime > 0) {
          await this.onSeek(targetTime);
        }
      }
    }
    
    // Just resume/play if already on the correct track
    if (!currentlyPlaying) {
      if (this._isLoadingTrack) {
        this.log('handlePlay: Track load in progress — skipping resume');
        return;
      }
      this.log('Starting playback via onPlay');
      if (this.onPlay) await this.onPlay();
    }
    
    // Seek if needed
    const currentTime = musicKitState?.playbackTime || 0;
    if (this.needsSeek(currentTime, targetTime)) {
      this.log(`Seeking from ${currentTime} to ${targetTime}`);
      if (this.onSeek) await this.onSeek(targetTime);
    }
    
    this.log('=== handlePlay END ===');
  }
  
  /**
   * PAUSE: Host paused playback
   */
  async handlePause(syncData, musicKitState) {
    const { current_position } = syncData;
    
    this.log('=== handlePause START ===');
    this.log(`current_position: ${current_position}`);
    this.log(`Callback available - onPause: ${!!this.onPause}`);
    
    // Set paused flag - this prevents auto-play effects from overriding
    this.isPausedByHost = true;
    
    // Always try to pause regardless of current state
    if (this.onPause) {
      this.log('Calling onPause callback...');
      await this.onPause();
      this.log('onPause completed');
    } else {
      this.log('WARNING: onPause callback not available!');
    }
    
    this.log('=== handlePause END ===');
  }
  
  /**
   * SEEK: Host seeked to a new position
   */
  async handleSeek(syncData, musicKitState) {
    const { position, is_playing, server_timestamp } = syncData;
    
    const targetTime = this.getTargetTimestamp(position, server_timestamp, is_playing);
    const currentTime = musicKitState?.playbackTime || 0;
    
    this.log(`SEEK: TargetTime=${targetTime.toFixed(1)}s, CurrentTime=${currentTime.toFixed(1)}s`);
    
    // Always seek for intentional seeks (host explicitly seeked)
    if (this.onSeek) {
      try {
        await this.onSeek(targetTime);
        this.log('SEEK: Completed');
      } catch (e) {
        this.log(`SEEK: Error: ${e.message}`);
      }
    }
  }
  
  /**
   * TRACK_CHANGE: Host changed to a new track
   * Uses loadSongAtPosition with position 0 for seamless transition
   */
  async handleTrackChange(syncData, musicKitState) {
    const { track_id, track_info, is_playing } = syncData;
    const currentTrackId = musicKitState?.currentTrackId;
    
    this.log(`TRACK_CHANGE: NewTrack=${track_id}, CurrentTrack=${currentTrackId}, ShouldPlay=${is_playing}`);
    
    // Only load if different track
    if (track_id && track_id !== currentTrackId) {
      this.log(`TRACK_CHANGE: Loading track ${track_id} at position 0`);
      
      // Use loadSongAtPosition for seamless transition (mutes during load)
      if (this.onTrackChangeAtPosition && is_playing) {
        this._isLoadingTrack = true;
        try {
          await this.onTrackChangeAtPosition(track_id, 0, track_info);
        } finally {
          this._isLoadingTrack = false;
        }
        this.log('TRACK_CHANGE: Track loaded via onTrackChangeAtPosition');
      } else if (this.onTrackChange) {
        // Fallback to old method
        await this.onTrackChange(track_id, track_info);
        this.log('TRACK_CHANGE: Track loaded via onTrackChange (fallback)');
      }
    } else {
      this.log('TRACK_CHANGE: Same track, no action needed');
    }
  }
  
  /**
   * SYNC_UPDATE: Periodic sync check - correct drift and wrong track
   */
  async handleSyncUpdate(syncData, musicKitState) {
    const { current_position, is_playing, server_timestamp, track_id } = syncData;
    
    // Skip SYNC_UPDATE during cooldown after INITIAL_SYNC
    // INITIAL_SYNC triggers loadSongAtPosition which takes time on mobile —
    // a premature SYNC_UPDATE would interrupt the in-progress load
    if (this._initialSyncTime && (Date.now() - this._initialSyncTime) < this._initialSyncCooldown) {
      this.log('SYNC_UPDATE: Skipping — within initial sync cooldown');
      return;
    }
    
    // Skip if a track load is already in progress — prevents aborting MusicKit operations
    if (this._isLoadingTrack) {
      this.log('SYNC_UPDATE: Track loading in progress — skipping to avoid abort');
      return;
    }
    
    // If we're on a different track (or no track loaded at all), fix it immediately.
    // The !musicKitState?.currentTrackId check handles the case where MusicKit
    // hasn't loaded anything yet (e.g., after a failed loadSongAtPosition).
    if (track_id && (!musicKitState?.currentTrackId || track_id !== musicKitState.currentTrackId)) {
      this.log(`SYNC_UPDATE: Wrong track! Expected ${track_id}, have ${musicKitState?.currentTrackId || 'null'} - correcting`);
      if (this.onTrackChangeAtPosition) {
        const targetTime = this.getTargetTimestamp(current_position, server_timestamp, is_playing);
        await this.onTrackChangeAtPosition(track_id, targetTime, syncData.track_info);
        return;
      } else if (this.onTrackChange) {
        await this.onTrackChange(track_id, syncData.track_info);
        return;
      }
    }
    
    // If guest is not playing but server says they should be, try to resume
    if (!musicKitState?.isPlaying) {
      if (is_playing && !this.isPausedByHost) {
        // Rate limit resume attempts (max once per 5 seconds)
        const now = Date.now();
        if (!this._lastResumeAttempt || (now - this._lastResumeAttempt) > 5000) {
          this._lastResumeAttempt = now;
          // Check if guest has the wrong track loaded (or no track at all) — then do a full reload
          if (track_id && (!musicKitState?.currentTrackId || track_id !== musicKitState.currentTrackId)) {
            this.log('SYNC_UPDATE: Wrong track while stopped — reloading correct track');
            if (this.onTrackChangeAtPosition) {
              const targetTime = this.getTargetTimestamp(current_position, server_timestamp, is_playing);
              await this.onTrackChangeAtPosition(track_id, targetTime, syncData.track_info);
            }
          } else if (this.onPlay) {
            // Correct track, just buffering/stalled — gentle resume
            // MusicKit handles its own re-buffering; we just nudge it to resume
            this.log('SYNC_UPDATE: Correct track but stopped — gentle resume');
            await this.onPlay();
          }
        }
      }
      return;
    }
    
    // If guest IS playing but server says NOT playing, pause MusicKit.
    // This catches missed PAUSE messages (e.g., WebSocket was down during host pause).
    if (musicKitState?.isPlaying && !is_playing) {
      this.log('SYNC_UPDATE: Server paused but guest still playing — pausing');
      this.isPausedByHost = true;
      if (this.onPause) {
        await this.onPause();
      }
      return;
    }
    
    const targetTime = this.getTargetTimestamp(current_position, server_timestamp, is_playing);
    const currentTime = musicKitState?.playbackTime || 0;
    const drift = Math.abs(currentTime - targetTime);
    
    // Correct if drift is greater than 3 seconds
    // (3s tolerates normal clock differences and network jitter without false-positive seeks)
    if (drift > 3) {
      this.log(`SYNC_UPDATE: Drift detected (${drift.toFixed(1)}s), correcting`);
      if (this.onSeek) {
        try {
          await this.onSeek(targetTime);
        } catch (e) {
          this.log(`SYNC_UPDATE: Seek error: ${e.message}`);
        }
      }
    }
  }
}

// Singleton instance
const audioSyncHandler = new AudioSyncHandler();
export default audioSyncHandler;
